'use client'

import { useState, useEffect } from 'react'
import { Property, Application } from '@/types'
import { useCurrency } from '@/lib/currency'
// Stripe.js not needed - we use direct URL redirect

interface ApplicationWithProperty extends Application {
  property?: Property
}

interface PaymentInterfaceProps {
  clientId: string
}

export default function PaymentInterface({ clientId }: PaymentInterfaceProps) {
  const { currency } = useCurrency()
  const [applications, setApplications] = useState<ApplicationWithProperty[]>([])
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Stripe is the only payment method supported in this build

  // Load payments and applications
  const loadData = async () => {
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        console.warn('No auth token found')
        setApplications([])
        return
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      // Fetch applications with status 'approved' or 'accepted' for payment
      const response = await fetch(`${apiUrl}/applications?status=approved`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch applications: ${response.statusText}`)
      }

      const data = await response.json()
      // Transform backend format to frontend format
      const transformedApplications = (data.applications || []).map((app: any) => ({
        ...app,
        id: app._id || app.id,
        property: app.property ? {
          ...app.property,
          id: app.property._id || app.property.id,
          landlord_id: app.property.landlord?._id || app.property.landlord || app.property.landlordId,
          is_available: app.property.isAvailable !== false
        } : null
      }))
      
      setApplications(transformedApplications)
    } catch (error) {
      console.error('Error loading applications:', error)
      setApplications([])
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId])

  // Helper function to get user-friendly error message
  const getErrorMessage = (error: any, defaultMessage: string): string => {
    // Network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return 'Network error: Unable to connect to payment server. Please check your internet connection and try again.'
    }
    
    // Timeout errors
    if (error?.name === 'AbortError' || error?.message?.includes('timeout')) {
      return 'Request timeout: The payment server took too long to respond. Please try again.'
    }
    
    // Stripe-specific errors
    if (error?.type === 'StripeCardError') {
      return `Card error: ${error.message || 'Your card was declined. Please check your card details and try again.'}`
    }
    
    if (error?.type === 'StripeRateLimitError') {
      return 'Too many requests: Please wait a moment and try again.'
    }
    
    if (error?.type === 'StripeInvalidRequestError') {
      return `Invalid payment request: ${error.message || 'Please check your payment details and try again.'}`
    }
    
    if (error?.type === 'StripeAPIError') {
      return 'Payment service error: There was an issue with the payment processor. Please try again later or contact support.'
    }
    
    if (error?.type === 'StripeConnectionError') {
      return 'Connection error: Unable to connect to payment service. Please check your internet connection and try again.'
    }
    
    if (error?.type === 'StripeAuthenticationError') {
      return 'Authentication error: Payment service authentication failed. Please contact support.'
    }
    
    // HTTP status code errors
    if (error?.status === 400) {
      return error?.message || 'Invalid payment request. Please check your payment details.'
    }
    
    if (error?.status === 401 || error?.status === 403) {
      return 'Authentication error: Please log in again and try again.'
    }
    
    if (error?.status === 404) {
      return 'Application not found. Please refresh the page and try again.'
    }
    
    if (error?.status === 429) {
      return 'Too many requests: Please wait a moment before trying again.'
    }
    
    if (error?.status === 500 || error?.status === 502 || error?.status === 503) {
      return 'Server error: Payment service is temporarily unavailable. Please try again in a few moments.'
    }
    
    // Return default or error message
    return error?.message || defaultMessage
  }

  // Make payment via Stripe Checkout
  const makePayment = async () => {
    // Reset error state
    setError(null)
    
    // Validation
    if (!selectedApplication || !paymentAmount) {
      setError('Please fill in all required fields')
      return
    }

    // Validate amount
    const amount = parseFloat(paymentAmount)
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid payment amount greater than zero')
      return
    }

    // Minimum amount check (e.g., 100 NGN)
    if (amount < 100) {
      setError('Payment amount must be at least ₦100')
      return
    }

    setLoading(true)

    try {
      const application = applications.find(app => app.id === selectedApplication)
      
      if (!application) {
        setError('Application not found. Please refresh the page and try again.')
        setLoading(false)
        return
      }

      if (!application.property) {
        setError('Property information not found. Please contact support.')
        setLoading(false)
        return
      }

      if (application.property.is_available === false) {
        setError('This property is currently unavailable. Please contact the landlord or choose another property.')
        setLoading(false)
        return
      }

      // Get the current user's authentication token
      const token = localStorage.getItem('authToken')
      
      if (!token) {
        setError('Please log in to make a payment')
        setLoading(false)
        return
      }

      // Create Stripe Checkout session through backend
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      
      // Add timeout to prevent hanging requests
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
      
      try {
        const res = await fetch(`${apiUrl}/payments/create-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            applicationId: selectedApplication,
            amount: paymentAmount,
            currency: currency.toLowerCase(),
          }),
          signal: controller.signal
        })

        clearTimeout(timeoutId)

        // Check if response is actually JSON
        const contentType = res.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          const text = await res.text()
          console.error('API returned non-JSON response:', contentType, text.substring(0, 500))
          setError('Server error: Unexpected response format. Please contact support.')
          setLoading(false)
          return
        }

        const data = await res.json()
        
        if (!res.ok) {
          // Handle different error types from backend
          const errorMessage = getErrorMessage(
            { type: data.error, message: data.message, status: res.status },
            'Failed to initialize payment. Please try again.'
          )
          setError(errorMessage)
          setLoading(false)
          return
        }

        // Validate response data
        if (!data.url) {
          console.error('❌ No checkout URL in response:', data)
          setError('Payment session created but no checkout URL available. Please contact support.')
          setLoading(false)
          return
        }

        // Log session creation for debugging
        console.log('✅ Stripe session created:', {
          sessionId: data.sessionId,
          url: data.url
        })

        // Close form before redirect
        setShowPaymentForm(false)
        resetForm()
        setLoading(false)
        
        // Redirect to Stripe checkout
        window.location.href = data.url
      } catch (fetchError: any) {
        clearTimeout(timeoutId)
        
        // Handle abort/timeout
        if (fetchError.name === 'AbortError') {
          setError('Request timeout: The payment server took too long to respond. Please try again.')
          setLoading(false)
          return
        }
        
        // Handle network errors
        if (fetchError instanceof TypeError && fetchError.message.includes('fetch')) {
          setError('Network error: Unable to connect to payment server. Please check your internet connection and try again.')
          setLoading(false)
          return
        }
        
        throw fetchError // Re-throw to be caught by outer catch
      }
    } catch (error: any) {
      console.error('Payment error:', error)
      const errorMessage = getErrorMessage(error, 'An unexpected error occurred while processing your payment. Please try again or contact support if the problem persists.')
      setError(errorMessage)
      setLoading(false)
    }
  }

  // Reset form
  const resetForm = () => {
    setSelectedApplication('')
    setPaymentAmount('')
  }

  // Currency available if needed later for labels
  // No-op placeholder
  // const formatCurrency = (amount: number) => format(amount)

  // Totals removed (Stripe handles checkout; we keep UI minimal)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Stripe Payments</h2>
          {/* <button
            onClick={() => setShowPaymentForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
          Pay with Stripe
          </button> */}
      </div>

      {/* Minimal UI – rely on Stripe checkout */}

      {/* Payment history removed in Stripe-only UI */}

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Make Rent Payment</h3>
                <button
                  onClick={() => setShowPaymentForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Error message display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-800">{error}</p>
                        <button
                          onClick={() => setError(null)}
                          className="text-xs text-red-600 hover:text-red-800 mt-1 underline"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property *
                  </label>
                  <select
                    value={selectedApplication}
                    onChange={(e) => {
                      setSelectedApplication(e.target.value)
                      setError(null) // Clear error when user changes selection
                    }}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  >
                    <option value="">Select a property</option>
                    {applications.map((application) => (
                      <option 
                        key={application.id} 
                        value={application.id}
                        disabled={application.property?.is_available === false}
                      >
                        {application.property?.title} - {application.property?.location}
                        {application.property?.is_available === false ? ' (Unavailable)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                  </label>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => {
                      setPaymentAmount(e.target.value)
                      setError(null) // Clear error when user changes amount
                    }}
                    placeholder="0.00"
                    min="100"
                    step="0.01"
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum amount: ₦100</p>
                </div>

                {/* Stripe Checkout is used for payments */}

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={makePayment}
                    disabled={loading || !selectedApplication || !paymentAmount}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Submit Payment'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowPaymentForm(false)
                      setError(null)
                      resetForm()
                    }}
                    disabled={loading}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors disabled:bg-gray-200 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

